import React, { type MouseEvent, type ReactNode, useState } from 'react';

import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ItemWrapper } from '@atlassian/people-teams-ui/profile-layout';
import { SkeletonList } from '@atlassian/ptc-common';

import Anchor from '../Anchor';
import { PanelContent, PanelContentHeader, PanelContentTitle } from '../PanelContent';
import WorkItem, { type WorkItemProps } from '../WorkItem';

import * as Styled from './WorkList.styled';

export interface WorkListProps {
	workItems: WorkItemProps[];
	isInitiallyExpanded: boolean;
	limitDefault?: number;
	limitExpanded?: number;
	viewAllLink?: string;
	emptyView: ReactNode;
	titleText?: ReactNode | string;
	infoText?: string | ReactNode | null;
	isLoading?: boolean;
	onClickViewAllLink?: (location: ButtonLocation) => void;
	onClickShowMore?: (workItemsCount: number) => void;
	onClickActivityEntry?: (provider: string, entryIndex: number) => void;
}

export type ButtonLocation = 'header' | 'footer';

const styles = cssMap({
	workListItemsWrapper: {
		borderRadius: token('border.radius.100'),
		boxShadow: token('elevation.shadow.raised'),
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		listStyle: 'none',
	},
	workListItemsNext: {
		listStyle: 'none',
		paddingLeft: token('space.0'),
	},
	skeletonList: {
		paddingInlineStart: token('space.100', '8px'),
		paddingInlineEnd: token('space.100', '8px'),
	},
});

export const WorkList = (props: WorkListProps) => {
	const [isExpanded, setIsExpanded] = useState<boolean>(props.isInitiallyExpanded);

	const { formatMessage } = useIntl();

	const onShowMore = (e: MouseEvent): void => {
		e.preventDefault();
		const { workItems, onClickShowMore, limitExpanded } = props;
		setIsExpanded(true);

		const workItemsCount = workItems.length;
		if (onClickShowMore) {
			onClickShowMore(
				// No limit? Show workItems count, otherwise show smaller of limit vs count
				!limitExpanded
					? workItemsCount
					: workItemsCount <= limitExpanded
						? workItemsCount
						: limitExpanded,
			);
		}
	};

	const onClickViewAllLink = (location: ButtonLocation) => () => {
		if (props.onClickViewAllLink) {
			props.onClickViewAllLink(location);
		}
	};

	const onClickActivityEntry = (provider: string, entryIndex: number) => () => {
		if (props.onClickActivityEntry) {
			props.onClickActivityEntry!(provider, entryIndex);
		}
	};

	const renderShowMoreButton = (): React.ReactNode => (
		<Styled.FooterLink>
			<Anchor
				appearance="subtle"
				onClick={onShowMore}
				href="#"
				aria-label={formatMessage(messages.showMoreLinkLabel)}
			>
				{formatMessage(messages.showMoreLink)}
			</Anchor>
		</Styled.FooterLink>
	);

	const renderViewAllFooterButton = () => (
		<Styled.FooterLink data-testid="view-all-link__footer">
			{renderViewAllLink({ appearance: 'subtle', location: 'footer' })}
		</Styled.FooterLink>
	);

	const renderViewAllHeaderButton = () =>
		props.workItems.length > 0 &&
		props.viewAllLink && (
			<Styled.HeaderLink data-testid="view-all-link__header">
				{renderViewAllLink({ location: 'header' })}
			</Styled.HeaderLink>
		);

	const renderViewAllLink = (linkOptions: { location: ButtonLocation; appearance?: string }) => {
		const { appearance = 'default', location } = linkOptions;
		return (
			<Anchor
				appearance={appearance}
				href={props.viewAllLink}
				onClick={onClickViewAllLink(location)}
				aria-label={formatMessage(messages.viewAllLinkLabel)}
			>
				<FormattedMessage {...messages.viewAllLink} />
			</Anchor>
		);
	};

	const renderFooterButtons = () => {
		const { viewAllLink, workItems, limitDefault } = props;
		const workItemsCount = workItems.length;
		const canExpand = workItemsCount > limitDefault!;

		if (workItemsCount === 0) {
			return null;
		}
		if (!isExpanded && canExpand) {
			return renderShowMoreButton();
		}
		if (viewAllLink) {
			return renderViewAllFooterButton();
		}
		return null;
	};

	const renderWorkItems = (): React.ReactNode => {
		const { workItems, limitDefault, limitExpanded, isLoading } = props;

		if (isLoading) {
			return (
				<Styled.SkeletonWrapper>
					<SkeletonList
						hasIcon
						isShimmering
						itemCount={limitDefault || 0}
						xcss={styles.skeletonList}
					/>
				</Styled.SkeletonWrapper>
			);
		}

		if (!workItems.length) {
			return null;
		}

		const limit = isExpanded ? limitExpanded : limitDefault;

		return workItems
			.slice(0, limit)
			.map((resourceItem, idx) => (
				<WorkItem
					key={`work-item-${idx}`}
					objectName={resourceItem.objectName}
					description={resourceItem.description}
					objectUrl={resourceItem.objectUrl}
					objectIconUrl={resourceItem.objectIconUrl}
					containerName={resourceItem.containerName}
					provider={resourceItem.provider}
					onClick={onClickActivityEntry(resourceItem.provider, idx)}
				/>
			));
	};

	const { titleText, infoText, workItems, emptyView } = props;

	return (
		<PanelContent data-testid="profile-work-list">
			<PanelContentHeader hasSubInfo={!!infoText} data-testid="profile-work-list-header">
				<PanelContentTitle>{titleText || formatMessage(messages.listTitle)}</PanelContentTitle>
				{renderViewAllHeaderButton()}
			</PanelContentHeader>
			{infoText && (
				<Styled.WorkListInfoText data-testid="profile-work-list-info">
					{infoText}
				</Styled.WorkListInfoText>
			)}
			{fg('enable_team_profile_heading_a11y_fix') ? (
				!workItems.length ? (
					emptyView
				) : (
					<ItemWrapper>
						<Box as="ul" xcss={styles.workListItemsNext}>
							{renderWorkItems()}
							{renderFooterButtons()}
						</Box>
					</ItemWrapper>
				)
			) : !workItems.length ? (
				emptyView
			) : (
				<Styled.WorkListItems>
					{renderWorkItems()}
					{renderFooterButtons()}
				</Styled.WorkListItems>
			)}
		</PanelContent>
	);
};

WorkList.defaultProps = {
	isInitiallyExpanded: false,
	workItems: [],
	limitDefault: 5,
	limitExpanded: 10,
	onClickViewAllLink: () => null,
	onClickShowMore: () => null,
	onClickActivityEntry: () => null,
};

export const messages = defineMessages({
	listTitle: {
		id: 'ptc-directory.profile-page.work-list.title',
		defaultMessage: 'Worked on',
	},
	showMoreLink: {
		id: 'ptc-directory.profile-page.work-list.show-more.link',
		defaultMessage: 'Show more',
		description: 'Link to reveal more work associated to the profile that is being viewed',
	},
	showMoreLinkLabel: {
		id: 'ptc-directory.profile-page.work-list.show-more.label',
		defaultMessage: 'Show more about worked on pages',
		description:
			'Helper text for screen reader users. It is announced by screen reader when user focus the show more link',
	},
	viewAllLink: {
		id: 'ptc-directory.profile-page.work-list.view-all.link',
		defaultMessage: 'View all',
		description: 'Link to navigate to Work child page',
	},
	viewAllLinkLabel: {
		id: 'ptc-directory.profile-page.work-list.view-all.label',
		defaultMessage: 'View all worked on pages',
		description:
			'Helper text for screen reader users. It is announced by screen reader when user focus the view all link',
	},
});

export default WorkList;
